import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
} from '@rematter/ui-web';
import { Trash2 } from 'lucide-react';

import { useTranslations } from '@/finance/i18n/useTranslations';

interface VoidPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  paymentDocumentNumber: string;
  linkedBillCount: number;
  linkedInvoiceCount: number;
  hasFinancialAccount: boolean;
}

export const VoidPaymentDialog: React.FC<VoidPaymentDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  paymentDocumentNumber,
  linkedBillCount,
  linkedInvoiceCount,
  hasFinancialAccount,
}) => {
  const t = useTranslations();
  const [isLoading, setIsLoading] = React.useState(false);

  const hasCascadeEffects =
    linkedBillCount > 0 || linkedInvoiceCount > 0 || hasFinancialAccount;

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error voiding payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={() => onClose()}
      className="@VoidPaymentDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle>
            {t.payments.detail.voidDialog.title} #{paymentDocumentNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogMain>
          <div className="flex flex-col gap-2">
            <div className="text-base text-neutral/50">
              {t.payments.detail.voidDialog.message}
            </div>
            {hasCascadeEffects && (
              <ul className="list-disc pl-5 text-sm text-neutral/50">
                {linkedBillCount > 0 && (
                  <li>
                    {t.payments.detail.voidDialog.cascadeEffects.unlinkBills(
                      linkedBillCount,
                    )}
                  </li>
                )}
                {linkedInvoiceCount > 0 && (
                  <li>
                    {t.payments.detail.voidDialog.cascadeEffects.unlinkInvoices(
                      linkedInvoiceCount,
                    )}
                  </li>
                )}
                {hasFinancialAccount && (
                  <li>
                    {t.payments.detail.voidDialog.cascadeEffects.restoreBalance}
                  </li>
                )}
              </ul>
            )}
          </div>
        </DialogMain>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button variant="text" color="neutral" onPress={onClose}>
              {t.payments.detail.voidDialog.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="failure"
              onPress={handleConfirm}
              icon={<Trash2 className="size-4" />}
              isDisabled={isLoading}
            >
              {t.payments.detail.voidDialog.buttons.void}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
